import { ChecklistProvider } from '@tour-kit/checklists';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  createGetStartedPersistence,
  type GetStartedChecklistSlice,
} from '@/core/guidance/checklist-persistence';
import {
  GET_STARTED_CHECKLIST_ID,
  type GetStartedTaskId,
} from '@/core/guidance/constants';
import { trackGuidanceEvent } from '@/core/guidance/guidance-analytics';
import { useGuidanceStore } from '@/core/guidance/guidance.store';
import { GetStartedCompletionSync } from '@/core/guidance/get-started-completion-sync';
import {
  buildGetStartedChecklist,
  type GetStartedLabels,
} from '@/core/guidance/get-started-checklist';
import {
  GetStartedChecklistUi,
  GuidanceChecklistLauncherPlaceholder,
} from '@/core/guidance/get-started-checklist-ui';
import { ChecklistHintGateSync } from '@/core/guidance/checklist-hint-gate-sync';
import { useGetStartedDomain } from '@/core/guidance/use-get-started-domain';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

/**
 * Get started checklist UI + completion sync. Rendered beside the shell (not
 * around it): the provider below is keyed and remounts on workspace,
 * permission or skip changes, which must only reset the checklist.
 */
export function GetStartedChecklistRoot() {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const dict = useDictionary();
  const domain = useGetStartedDomain();
  const { navigateShell } = useNavigateShell();
  const hideForWizardFooter = useGuidanceStore((s) => s.isWizardFooterActive);

  const [slice, setSlice] = useState<GetStartedChecklistSlice | null>(null);
  const persistence = useMemo(
    () => (workspaceId ? createGetStartedPersistence(workspaceId) : null),
    [workspaceId],
  );

  if (!persistence && slice !== null) {
    setSlice(null);
  }

  useEffect(() => {
    if (!persistence) return;
    let cancelled = false;
    void persistence.hydrate().then((loaded) => {
      if (!cancelled) setSlice(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [persistence]);

  const labels = useMemo<GetStartedLabels>(() => {
    const c = dict.guidance.checklist;
    return {
      title: c.title,
      description: c.description,
      tasks: {
        'ws.location': c.tasks['ws.location'],
        'ws.product': c.tasks['ws.product'],
        'ws.google': c.tasks['ws.google'],
        'ws.invite': c.tasks['ws.invite'],
        'me.profile': c.tasks['me.profile'],
        'me.card': c.tasks['me.card'],
        'me.tour': c.tasks['me.tour'],
        'me.share': c.tasks['me.share'],
      },
    };
  }, [dict.guidance.checklist]);

  const skipped = useMemo(
    () => new Set<GetStartedTaskId>(slice?.skipped ?? []),
    [slice?.skipped],
  );

  const checklist = useMemo(() => {
    if (!domain.isDomainReady || !slice) return null;
    return buildGetStartedChecklist({
      labels,
      domain,
      skipped,
      intent: slice.intent,
      actions: {
        navigate: navigateShell,
        startOrientationTour: () => {
          useGuidanceStore.getState().requestOrientationReplay();
        },
      },
    });
  }, [domain, labels, navigateShell, skipped, slice]);

  const providerKey = useMemo(() => {
    if (!workspaceId || !checklist) return null;
    return [
      workspaceId,
      slice?.intent ?? 'full',
      [...skipped].sort().join(','),
      domain.canManageLocations ? '1' : '0',
      domain.canManageProducts ? '1' : '0',
      domain.canInviteMembers ? '1' : '0',
      domain.canSetupReputation ? '1' : '0',
      checklist.tasks.map((task) => task.id).join(','),
    ].join('|');
  }, [workspaceId, skipped, domain, checklist, slice?.intent]);

  const handleSkip = useCallback(
    async (taskId: GetStartedTaskId) => {
      if (!persistence) return;
      await persistence.skipTask(taskId);
      setSlice({ ...persistence.getSlice() });
      trackGuidanceEvent({
        name: 'checklist_task_skipped',
        metadata: {
          checklistId: GET_STARTED_CHECKLIST_ID,
          taskId,
        },
      });
    },
    [persistence],
  );

  if (!workspaceId || !persistence || !checklist || !providerKey || !slice) {
    return hideForWizardFooter ? null : (
      <GuidanceChecklistLauncherPlaceholder />
    );
  }

  return (
    <ChecklistProvider
      key={providerKey}
      checklists={[checklist]}
      persistence={{
        enabled: true,
        storage: 'memory',
        onSave: persistence.onSave,
        onLoad: persistence.onLoad,
      }}
      onTaskComplete={(checklistId, taskId) => {
        trackGuidanceEvent({
          name: 'checklist_task_completed',
          metadata: { checklistId, taskId },
        });
      }}
      onChecklistComplete={(checklistId) => {
        trackGuidanceEvent({
          name: 'checklist_completed',
          metadata: { checklistId },
        });
      }}
      onChecklistDismiss={(checklistId) => {
        trackGuidanceEvent({
          name: 'checklist_dismissed',
          metadata: { checklistId },
        });
      }}
      onTaskAction={(checklistId, taskId) => {
        trackGuidanceEvent({
          name: 'checklist_task_action',
          metadata: { checklistId, taskId },
        });
      }}
    >
      <GetStartedCompletionSync
        domain={domain}
        shareDone={Boolean(slice.personal.shareDone)}
      />
      {hideForWizardFooter ? null : (
        <GetStartedChecklistUi
          onSkip={handleSkip}
          onSharePersisted={async () => {
            await persistence.markShareDone();
            setSlice({ ...persistence.getSlice() });
          }}
        />
      )}
      <ChecklistHintGateSync shareDone={Boolean(slice.personal.shareDone)} />
    </ChecklistProvider>
  );
}
